// src/components/Contact/Contact.js
import React, { useState } from 'react';
import Header from '../Header/Header';
import Footer from '../Footer/Footer';
import './Contact.css';
import contactApi from '../../api/contactApi';

const Contact = () => {
  const [formData, setFormData] = useState({
    nom: '',
    email: '',
    telephone: '',
    date_contact: '',
    sujet: '',
    notes: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.nom.trim()) {
      newErrors.nom = 'Le nom est requis';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'L\'email est requis';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email invalide';
    }

    if (!formData.telephone.trim()) {
      newErrors.telephone = 'Le téléphone est requis';
    }

    if (!formData.date_contact) {
      newErrors.date_contact = 'La date du contact est requise';
    }

    if (!formData.sujet.trim()) {
      newErrors.sujet = 'Le sujet est requis';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const formattedData = {
        ...formData,
        date_contact: formData.date_contact.replace('T', ' ') + ':00'
      };

      const response = await contactApi.sendContact(formattedData);
      
      setSubmitStatus('success');
      setSubmitMessage(response.message || 'Votre demande a été envoyée avec succès !');
      
      setFormData({
        nom: '',
        email: '',
        telephone: '',
        date_contact: '',
        sujet: '',
        notes: ''
      });
      
      setTimeout(() => {
        setSubmitStatus(null);
        setSubmitMessage('');
      }, 5000);
      
    } catch (error) {
      setSubmitStatus('error');
      setSubmitMessage(error.message || 'Une erreur est survenue. Veuillez réessayer.');
      
      setTimeout(() => {
        setSubmitStatus(null);
        setSubmitMessage('');
      }, 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Header />
      <div className="contact-page">
        <div className="contact-hero">
          <div className="contact-hero-content">
            <h1>Nous Contacter</h1>
            <p>Une question ? Un projet ? Contactez-nous et notre équipe vous répondra dans les plus brefs délais.</p>
          </div>
        </div>

        <div className="contact-wrapper">
          <div className="contact-container">
            <div className="contact-grid">
              {/* Formulaire */}
              <div className="contact-form-container">
                <h2>Envoyez-nous un message</h2>
                <form className="contact-form" onSubmit={handleSubmit}>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="nom">Nom complet *</label>
                      <input
                        type="text"
                        id="nom"
                        name="nom"
                        value={formData.nom}
                        onChange={handleChange}
                        placeholder="Votre nom et prénom"
                        className={errors.nom ? 'error' : ''}
                      />
                      {errors.nom && <span className="error-message">{errors.nom}</span>}
                    </div>

                    <div className="form-group">
                      <label htmlFor="email">Email *</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="votre@email.com"
                        className={errors.email ? 'error' : ''}
                      />
                      {errors.email && <span className="error-message">{errors.email}</span>}
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="telephone">Téléphone *</label>
                      <input
                        type="tel"
                        id="telephone"
                        name="telephone"
                        value={formData.telephone}
                        onChange={handleChange}
                        placeholder="+33 7 63 70 50 88"
                        className={errors.telephone ? 'error' : ''}
                      />
                      {errors.telephone && <span className="error-message">{errors.telephone}</span>}
                    </div>

                    <div className="form-group">
                      <label htmlFor="date_contact">Date du contact *</label>
                      <input
                        type="datetime-local"
                        id="date_contact"
                        name="date_contact"
                        value={formData.date_contact}
                        onChange={handleChange}
                        className={errors.date_contact ? 'error' : ''}
                      />
                      {errors.date_contact && <span className="error-message">{errors.date_contact}</span>}
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group full-width">
                      <label htmlFor="sujet">Sujet *</label>
                      <input
                        type="text"
                        id="sujet"
                        name="sujet"
                        value={formData.sujet}
                        onChange={handleChange}
                        placeholder="Objet de votre demande"
                        className={errors.sujet ? 'error' : ''}
                      />
                      {errors.sujet && <span className="error-message">{errors.sujet}</span>}
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group full-width">
                      <label htmlFor="notes">Notes supplémentaires</label>
                      <textarea
                        id="notes"
                        name="notes"
                        value={formData.notes}
                        onChange={handleChange}
                        rows="5"
                        placeholder="Détails de votre demande..."
                      />
                    </div>
                  </div>

                  {submitStatus && (
                    <div className={`alert alert-${submitStatus}`}>
                      {submitMessage}
                    </div>
                  )}

                  <button 
                    type="submit" 
                    className="submit-btn"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Envoi en cours...' : 'Envoyer la demande'}
                  </button>
                </form>
              </div>

              {/* Informations de contact */}
              <div className="contact-info-container">
                <h2>Nos coordonnées</h2>
                <div className="contact-info-list">
                  <div className="contact-info-item">
                    <div className="info-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M22 6L12 13L2 6M22 6V18C22 18.5304 21.7893 19.0391 21.4142 19.4142C21.0391 19.7893 20.5304 20 20 20H4C3.46957 20 2.96086 19.7893 2.58579 19.4142C2.21071 19.0391 2 18.5304 2 18V6M22 6L12 13L2 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <div className="info-content">
                      <h3>Email</h3>
                      <a href="mailto:pole.admin@btgconseil.com">pole.admin@btgconseil.com</a>
                    </div>
                  </div>

                  <div className="contact-info-item">
                    <div className="info-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M22 16.92V19.92C22.0011 20.1985 21.9441 20.4742 21.8325 20.7294C21.7209 20.9845 21.5573 21.2136 21.352 21.4019C21.1467 21.5901 20.904 21.7335 20.6397 21.8227C20.3754 21.9119 20.0951 21.9451 19.818 21.92C16.7428 21.5856 13.787 20.5341 11.19 18.85C8.7739 17.3147 6.72533 15.2661 5.19 12.85C3.49992 10.2412 2.44807 7.271 2.12 4.18C2.09499 3.90339 2.12807 3.62348 2.21687 3.35955C2.30568 3.09562 2.44853 2.85315 2.63622 2.64792C2.82391 2.44269 3.05235 2.27914 3.30685 2.16733C3.56135 2.05552 3.8364 1.99812 4.114 2H7.114C7.59521 1.99528 8.06209 2.15308 8.442 2.445C8.82191 2.73692 9.09171 3.14506 9.206 3.61C9.3875 4.38796 9.65091 5.14547 9.991 5.87C10.1369 6.20996 10.1909 6.58258 10.1471 6.94971C10.1033 7.31685 9.96329 7.66481 9.743 7.958L8.591 9.473C9.99739 11.8486 11.9852 13.8329 13.363 15.236L14.871 14.09C15.1661 13.8688 15.5163 13.7286 15.8856 13.6851C16.255 13.6417 16.6295 13.6967 16.971 13.844C17.6914 14.1818 18.4451 14.4437 19.219 14.626C19.6841 14.7408 20.0929 15.0113 20.385 15.392C20.677 15.7727 20.8349 16.2403 20.83 16.722L22 16.92Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <div className="info-content">
                      <h3>Téléphone</h3>
                      <a href="tel:+33763705088">+33 7 63 70 50 88</a>
                    </div>
                  </div>

                  <div className="contact-info-item">
                    <div className="info-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2C8.13 2 5 5.13 5 9C5 13.17 9.42 18.92 11.24 21.11C11.64 21.59 12.37 21.59 12.77 21.11C14.58 18.92 19 13.17 19 9C19 5.13 15.87 2 12 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M12 11.5C13.3807 11.5 14.5 10.3807 14.5 9C14.5 7.61929 13.3807 6.5 12 6.5C10.6193 6.5 9.5 7.61929 9.5 9C9.5 10.3807 10.6193 11.5 12 11.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <div className="info-content">
                      <h3>Adresse</h3>
                      <p>Paris, France</p>
                    </div>
                  </div>

                  <div className="contact-info-item">
                    <div className="info-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <div className="info-content">
                      <h3>Horaires</h3>
                      <p>Lun - Ven: 9h - 18h</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Contact;